/**
 * Business enumerations. Single source of truth: docs/data-dictionary.md §2.
 * Each enumeration is a const object plus a union type of the same name, so
 * both `RecordType.TimeBlock` and the type `RecordType` can be used.
 */

export const RecordType = {
  Project: 'project',
  TimeBlock: 'timeBlock',
  CheckinItem: 'checkinItem',
  CheckinLog: 'checkinLog',
  DailyNote: 'dailyNote',
  Template: 'template',
  Setting: 'setting',
  ImportantDate: 'importantDate',
} as const;
export type RecordType = (typeof RecordType)[keyof typeof RecordType];

/** How a check-in item recurs (data dictionary §2). Absent or null means every day; there is no `daily` value. */
export const CheckinRepeat = {
  Weekly: 'weekly',
  Interval: 'interval',
} as const;
export type CheckinRepeat = (typeof CheckinRepeat)[keyof typeof CheckinRepeat];

export const Category = {
  Work: 'work',
  Rest: 'rest',
  Life: 'life',
} as const;
export type Category = (typeof Category)[keyof typeof Category];
export const CATEGORIES: readonly Category[] = [Category.Work, Category.Rest, Category.Life];

export const BlockStatus = {
  Draft: 'draft',
  Confirmed: 'confirmed',
} as const;
export type BlockStatus = (typeof BlockStatus)[keyof typeof BlockStatus];

export const DraftSource = {
  Template: 'template',
  Repeat: 'repeat',
} as const;
export type DraftSource = (typeof DraftSource)[keyof typeof DraftSource];

/**
 * Time step in minutes (data dictionary §2 `TimeStep`): the grid pointer and keyboard
 * operations snap block edges to. Value of the setting key `timeStepMin`.
 */
export const TimeStep = {
  Min5: 5,
  Min15: 15,
  Min30: 30,
  Min60: 60,
} as const;
export type TimeStep = (typeof TimeStep)[keyof typeof TimeStep];
export const TIME_STEPS: readonly TimeStep[] = [
  TimeStep.Min5,
  TimeStep.Min15,
  TimeStep.Min30,
  TimeStep.Min60,
];

/**
 * Legacy enumeration: values of the old setting key `minUnit` (minimum unit in minutes). New
 * versions never write it; it is only read while both new keys (`timeStepMin`,
 * `minDurationMin`) are missing (data dictionary §3, migration rules).
 */
export const MinUnit = {
  Min15: 15,
  Min30: 30,
  Min60: 60,
} as const;
export type MinUnit = (typeof MinUnit)[keyof typeof MinUnit];
export const MIN_UNITS: readonly MinUnit[] = [MinUnit.Min15, MinUnit.Min30, MinUnit.Min60];

export const Locale = {
  ZhCN: 'zh-CN',
  En: 'en',
} as const;
export type Locale = (typeof Locale)[keyof typeof Locale];

/** Keys of the built-in default projects (data dictionary §4). */
export const DefaultProjectKey = {
  Work: 'work',
  Sleep: 'sleep',
  Chores: 'chores',
} as const;
export type DefaultProjectKey = (typeof DefaultProjectKey)[keyof typeof DefaultProjectKey];

/**
 * Result of a block-writing command (data dictionary §2 `MarkOutcome`, 2026-10b §2.5): the
 * caller awaits it and reacts — quick mark's "once" is consumed only on `Created`. Never persisted.
 */
/**
 * Why the picker's single commit point `flushPicker` ran (data dictionary §2, 2026-10b §5.4).
 * Never persisted. `Dismiss` = the user closed or left the picker (commit the fields and
 * close); `Delete` = the selected block is being deleted (commit nothing); `Hide` = the page
 * went hidden (commit the fields, keep the picker open); `Expire` = the target records
 * changed under sync (commit to the records that still exist, then close).
 */
export const PickerFlushReason = {
  Dismiss: 'dismiss',
  Delete: 'delete',
  Hide: 'hide',
  Expire: 'expire',
} as const;
export type PickerFlushReason = (typeof PickerFlushReason)[keyof typeof PickerFlushReason];

/**
 * Which context the session key `wiztime.pickerDraft` stashed (data dictionary §2 and §7.1):
 * `Create` = the record does not exist yet; `Existing` = a backstop for a commit that may not
 * have reached storage before the page froze.
 */
export const PickerDraftKind = {
  Create: 'create',
  Existing: 'existing',
} as const;
export type PickerDraftKind = (typeof PickerDraftKind)[keyof typeof PickerDraftKind];

export const MarkOutcome = {
  /** A new block was created. */
  Created: 'created',
  /** The exact-duplicate guard suppressed the creation (ui-redesign §2.1.4). */
  Duplicate: 'duplicate',
  /** Nothing to do: the block already has the chosen project. */
  Unchanged: 'unchanged',
  /** The write failed. */
  Failed: 'failed',
} as const;
export type MarkOutcome = (typeof MarkOutcome)[keyof typeof MarkOutcome];

/**
 * Which check-in items remind at their planned time (setting `reminderScope`, data dictionary §3):
 * `all` = every active item with a planned time; `selected` = only items whose `remind` is true.
 */
export const ReminderScope = {
  All: 'all',
  Selected: 'selected',
} as const;
export type ReminderScope = (typeof ReminderScope)[keyof typeof ReminderScope];

/** Minutes a reminder comes before the planned time (setting `reminderLeadMin`); 0 = on time. */
export const ReminderLead = {
  OnTime: 0,
  Min5: 5,
  Min10: 10,
  Min15: 15,
} as const;
export type ReminderLead = (typeof ReminderLead)[keyof typeof ReminderLead];
export const REMINDER_LEADS: readonly ReminderLead[] = Object.values(ReminderLead);

/** Kind of a revision entry of a check-in item or project (data dictionary §2, §3.1). */
export const RevisionOp = {
  Created: 'created',
  Changed: 'changed',
} as const;
export type RevisionOp = (typeof RevisionOp)[keyof typeof RevisionOp];

/**
 * Tracked field of a revision change (data dictionary §2, §3.1). `rule` stands for the five
 * recurrence fields of a check-in item compared and recorded as one value.
 */
export const RevisionField = {
  Name: 'name',
  PlannedMin: 'plannedMin',
  Rule: 'rule',
  Remind: 'remind',
  Active: 'active',
  Color: 'color',
  Category: 'category',
  QuickDurationMin: 'quickDurationMin',
} as const;
export type RevisionField = (typeof RevisionField)[keyof typeof RevisionField];

/** Category of an operation-log entry (data dictionary §2, §11). */
export const OpLogKind = {
  Command: 'command',
  Undo: 'undo',
  Redo: 'redo',
  Import: 'import',
  Clear: 'clear',
  Sync: 'sync',
  System: 'system',
  Migration: 'migration',
  Reminder: 'reminder',
  LocalSetting: 'localSetting',
} as const;
export type OpLogKind = (typeof OpLogKind)[keyof typeof OpLogKind];

/** A reminder decision recorded in the operation log (data dictionary §2, §11). */
export const ReminderDecision = {
  Shown: 'shown',
  Missed: 'missed',
  Silent: 'silent',
  Snoozed: 'snoozed',
  Done: 'done',
} as const;
export type ReminderDecision = (typeof ReminderDecision)[keyof typeof ReminderDecision];

/** Calendar system of an important date (data dictionary §2 `CalendarId`). */
export const CalendarId = {
  Gregorian: 'gregorian',
  ChineseLunar: 'chineseLunar',
} as const;
export type CalendarId = (typeof CalendarId)[keyof typeof CalendarId];
export const CALENDAR_IDS: readonly CalendarId[] = [CalendarId.Gregorian, CalendarId.ChineseLunar];

/** How an important date repeats (data dictionary §2 `RepeatKind`); unrelated to `CheckinRepeat`. */
export const RepeatKind = {
  None: 'none',
  Yearly: 'yearly',
  Monthly: 'monthly',
} as const;
export type RepeatKind = (typeof RepeatKind)[keyof typeof RepeatKind];
export const REPEAT_KINDS: readonly RepeatKind[] = [RepeatKind.None, RepeatKind.Yearly, RepeatKind.Monthly];

/** Kind of one holiday rule inside a holiday pack (data dictionary §2 `HolidayRuleKind`). */
export const HolidayRuleKind = {
  Fixed: 'fixed',
  NthWeekday: 'nthWeekday',
  SolarTerm: 'solarTerm',
  Computed: 'computed',
  Offset: 'offset',
} as const;
export type HolidayRuleKind = (typeof HolidayRuleKind)[keyof typeof HolidayRuleKind];

/** Named computed holiday rules (data dictionary §2 `ComputedRuleName`). */
export const ComputedRuleName = {
  Easter: 'easter',
  Chuxi: 'chuxi',
} as const;
export type ComputedRuleName = (typeof ComputedRuleName)[keyof typeof ComputedRuleName];

/** Built-in holiday packs (data dictionary §2 `HolidayPackId`). */
export const HolidayPackId = {
  CnTraditional: 'cn-traditional',
  CnSolarTerms: 'cn-solar-terms',
  Fr: 'fr',
} as const;
export type HolidayPackId = (typeof HolidayPackId)[keyof typeof HolidayPackId];

/** The 24 solar terms in order of the Gregorian year, 小寒 first (data dictionary §2 `SolarTermId`). */
export const SOLAR_TERM_IDS = [
  'xiaohan',
  'dahan',
  'lichun',
  'yushui',
  'jingzhe',
  'chunfen',
  'qingming',
  'guyu',
  'lixia',
  'xiaoman',
  'mangzhong',
  'xiazhi',
  'xiaoshu',
  'dashu',
  'liqiu',
  'chushu',
  'bailu',
  'qiufen',
  'hanlu',
  'shuangjiang',
  'lidong',
  'xiaoxue',
  'daxue',
  'dongzhi',
] as const;
export type SolarTermId = (typeof SOLAR_TERM_IDS)[number];
